import { appstr } from "../utils/appstr";
import {
  enumAssistOption,
  enumBGOption,
  enumChartTemplateSet,
  enumDataSource,
  enumHierarchyTypes,
  enumIWizDataSet,
  enumObjClassNames,
  enumSaveScope,
  kAdminUser,
  kMasterSCID,
  kMultiDocVersion
} from "../constants";
import { QAccessMgr } from "./QAccessMgr";
import { QDocPrefs } from "./QDocPrefs";
import { QIWizData } from "./QIWizData";
import { QMultiDocParams } from "./QMultiDocParams";
import { QObjDB } from "./QObjDB";
import { QPageMgr } from "./QPageMgr";
import { QProfileMgr } from "./QProfileMgr";
import { QRefMgr } from "./QRefMgr";
import { QSortConfig } from "./QSortConfig";
import { QViewMgr } from "./QViewMgr";
import { QXDoc } from "./QXDoc";
import { QXMLLoader } from "./QXMLLoader";
import { QXMLObject } from "./QXMLObject";
import { RmvDupsParentList } from "../utils/RmvDupsParentList";
import { SaveLoadFileAsJSON } from "../utils/SaveLoadFileAsJSON";
import { SetObjFilter } from "../utils/SetObjFilter";
import { StripExtension } from "../utils/StripExtension";
import { GetExtension } from "../utils/GetExtension";
import { GetStdDate } from "../utils/GetStdDate";
import { Globals } from "./Globals";
import { IsBackgroundFile } from "../utils/IsBackgroundFile";
import { QStream } from "./QStream.js";
import { MHAddParentToList } from "../utils/MHAddParentToList.js";
import { LoadFileAsXML } from "../utils/LoadFileAsXML.js";
import { PositionConfig } from "./PositionConfig";
import { IsPositionChart } from "../utils/IsPositionChart";
import { CreateFieldSourceMaps } from "../utils/CreateFieldSourceMaps";

/**
 * @typedef {import('./QXWSMgr').QXWSMgr} QXWSMgr
 */

export class QXDocMgr extends QXMLObject {
  constructor() {
    super();
    this.fDocList = null;
    this.fDocMgrVersion = kMultiDocVersion;

    this.fIWizDataRefresh = null;
    // Used with SubChart Wizard + Apply Master Page
    this.fChartMetadata = null;

    /**
     * @type {(null | QXWSMgr)}
     */
    this.fWSMgr = null;
    this.fCurDoc = null;

    this.fFileName = null;
    this.fDirectoryName = null;
    this.fDirLocked = null;

    this.fCurDocSave = null;

    this.fDirty = false;

    this.fCompoundParams = null;

    this.fProfileMgr = null;
    this.fViewMgr = null;

    this.fDocPrefs = null;
    this.fAccessMgr = null;

    this.fPageMgr = null;

    this.fLegacyBoxLayoutList = null;
    this.fRootDiv = null;

    this.fRefMgr = null;
    this.fRefreshDate = null;
    this.fRefreshTime = null;
    this.fAuthor = "none";

    this.fLastSearch = null;
    this.fShowMaster = true;

    this.fHeader = null;
    this.fDisconnectedBranches = [];
    this.fSaveInProgress = false;
    this.fSaveQueued = false;
  }

  IsServerChart() {
    if (!this.GetIWizDataInteractive()) return false;
    return this.GetIWizDataInteractive().GetIsServerChart();
  }

  IsLiveChart() {
    if (!this.GetIWizDataInteractive()) return false;
    return this.GetIWizDataInteractive().GetIsLiveChart();
  }

  IsManualChart() {
    var source = this.GetIWizDataInteractive().GetDataSource();
    return source == enumDataSource.manualchart;
  }

  IsPlanChart() {
    var iwizdata = this.GetIWizDataInteractive();
    if (iwizdata) var isPlan = iwizdata.fIsPlanChart == true;
    else return false;
    return isPlan;
  }

  InitDocMgr(wsMgr) {
    this.fDocList = [];
    this.fWSMgr = wsMgr;
    this.fCompoundParams = new QMultiDocParams();
    this.fCompoundParams.InitMultiDocParams(false, "", "", 0, true, true);

    this.fViewMgr = new QViewMgr();
    this.fViewMgr.InitViewMgr(wsMgr);

    this.fDocPrefs = new QDocPrefs();
    this.fDocPrefs.InitDocPrefs(this);

    this.InitAccessMgr();

    this.fProfileMgr = new QProfileMgr();
    this.fProfileMgr.InitProfileMgr(this);
    this.SetXMLElementName("qxdocmgr");

    this.fPageMgr = new QPageMgr();
    this.fPageMgr.InitPageMgr(this);

    this.fRefMgr = new QRefMgr();
    this.fRefMgr.InitRefMgr(wsMgr);

    this.StampRefreshDate();
  }

  GetWSMgr() {
    return this.fWSMgr;
  }

  PausePicQueue() {
    var n = this.GetNumDocuments();
    for (var i = 0; i < n; i++) {
      var d = this.GetIndexDoc(i);
      d.PausePicQueue();
    }
  }

  SetDataSource(
    dataSource //, importSort)
  ) {
    this.fChartMetadata = new QIWizData();
    this.fChartMetadata.InitIWizData(dataSource, enumIWizDataSet.interactive);
  }

  GetAccessMgr() {
    return this.fAccessMgr;
  }

  IsRefreshable() {
    var iw = this.fChartMetadata;

    var ds = iw.GetDataSource();
    return (
      ds != enumDataSource.manual &&
      ds != enumDataSource.pointintime &&
      ds != enumDataSource.background
    );
    //return (this.fIWizDataRefresh != null);
  }

  SlimDocMgr(iWizData) {
    // Check for preserve manual changes to layouts
    var pLayout = iWizData.GetPersistBoxLayout();
    if (pLayout) return;

    // Check for manual assistants
    var pAssist = iWizData.GetPersistAssist();
    if (pAssist) return;

    // Check for user pagination
    var pSCBreaks = iWizData.GetPersistSCBreak();
    if (pSCBreaks) return;

    // Check for branch styles
    var pBranch = iWizData.GetPersistBranchStyle();
    if (pBranch) return;

    // Check for box sort
    var pSort = iWizData.GetPersistBoxSort();
    if (pSort) return;

    // Check for locked boxes
    var d0 = this.GetIndexDoc(0);
    var curBoxCount = d0.GetCurBoxCount();
    if (curBoxCount < 3000) return;

    var n = this.GetNumDocuments();
    for (var i = 0; i < n; i++) {
      var d = this.GetIndexDoc(i);
      var okSlim = !d.AnyObjectLocked() && !d.AnyLayoutLocked();

      if (okSlim) d.SlimDocument();
    }
  }

  GetIWizDataRefresh() {
    return this.GetIWizDataInteractive();
  }

  SetIWizDataRefresh(iwizdata) {
    this.SetIWizDataInteractive(iwizdata);
  }

  GetIWizDataInteractive() {
    return this.fChartMetadata;
  }

  SetIWizDataInteractive(iwizdata) {
    this.fChartMetadata = iwizdata;
  }

  ClearIWizDataDocMgrReference() {
    if (this.fIWizDataRefresh) this.fIWizDataRefresh.SetPriorDocMgr(null);

    if (this.fChartMetadata) this.fChartMetadata.SetPriorDocMgr(null);
  }

  InitFromMetadata(metadata, wsMgr, launchSettings = {}) {
    this.LoadModDateAndTime(metadata);
    this.LoadiWizData(metadata, wsMgr, launchSettings);
    var doc = this.GetCurrentDoc();
    doc.HandleOptions(metadata, wsMgr);
    wsMgr.BroadcastTopic("chartMetadataLoaded", metadata);
    return true;
  }

  AddDisconnectedBranches(branches) {
    this.fDisconnectedBranches = branches;
  }

  LoadModDateAndTime(metadata) {
    this.LoadModDate(metadata);
    this.LoadModTime(metadata);
  }

  LoadModDate(jsonObj) {
    var modDate = jsonObj["Timestamp"];

    const isPIT = jsonObj["docType"] === "Archive";
    if (modDate != null && !isPIT) this.SetRefreshDate(modDate);
    else if (modDate && isPIT) this.SetRefreshDate(jsonObj["pointintime"]);
  }

  LoadModTime(jsonObj) {
    var modTime = jsonObj["Refreshtime"];

    const isPIT = jsonObj["docType"] === "Archive";
    if (modTime != null && !isPIT) this.SetRefreshTime(modTime);
    else if (modTime && isPIT) this.SetRefreshTime("null");
  }

  LoadiWizData(metadata, wsMgr, launchSettings = {}) {
    //Two general cases here, populating an empty/new document baseline, and updating across a sync
    var options = metadata["options"];
    var dataSource;

    if (metadata["import"]["source"] === "OPP") {
      metadata["doctype"] = "builder";
    }

    var docType = metadata["doctype"];
    var isLiveChart = true;
    var baseChartID = metadata["ID"];
    var archiveDate = null;
    switch (docType) {
      case "serverchart":
        dataSource = enumDataSource.serverchart;
        break;
      case "Archive":
        archiveDate = wsMgr.fUserInfo?.fPointInTime?.hotdate ?? null;
        dataSource = enumDataSource.pointintime;
        break;
      case "publish":
      case "builder":
        dataSource = enumDataSource.chartbuilder;
        isLiveChart = metadata["liveChart"];
        break;
      case "manualchart":
        dataSource = enumDataSource.manualchart;
        break;
      case "plan":
        dataSource = enumDataSource.serverchart;
        isLiveChart = metadata["liveChart"] ?? true;
        break;
      default:
        dataSource = enumDataSource.serverchart;
        isLiveChart = true;
        break;
    }

    var priorDocMetadata = wsMgr.fDataRefreshInfo;
    var docMgr = this;

    if (priorDocMetadata) {
      dataSource = priorDocMetadata.GetDataSource();
    }

    var curDoc = this.GetCurrentDoc();
    if (priorDocMetadata && dataSource === enumDataSource.chartbuilder) {
      var iWizData = priorDocMetadata;
      let priorDocMgr = priorDocMetadata.GetPriorDocMgr();
      let priorDoc = priorDocMgr.GetCurrentDoc();
      if (priorDoc.fSelfView) curDoc.fSelfView = priorDoc.fSelfView.Clone();
    } else {
      iWizData = new QIWizData();
      iWizData.InitIWizData(dataSource, enumIWizDataSet.interactive);
      iWizData.SetBaseChartID(baseChartID);
      iWizData.fIsLiveChart = metadata["liveChart"] ?? isLiveChart;
      iWizData.fIsServerChart = dataSource == enumDataSource.serverchart && iWizData.fIsLiveChart;
      iWizData.fIsPlanChart = docType === "plan";
      iWizData.fChartLevels = metadata["chartLevels"] || metadata["defaultlevel"] || 2;
      if (launchSettings.launchLevels) {
        iWizData.fChartLevels = launchSettings.launchLevels;
      }
      curDoc.SetRequestedLevels(iWizData.fChartLevels);
      var scRule = metadata["subchartrule"];
      var scSort = metadata["scSort"];
      iWizData.fChainLevels = metadata["chainLevels"] || 0;
      curDoc.SetRequestedChainLevels(iWizData.fChainLevels);
      var templateList = metadata["views"] ?? [];
      var defaultTemplate = metadata["svrtemplate"];
      if (launchSettings.launchView) {
        defaultTemplate = launchSettings.launchView;
      }
      var currentTemplate = defaultTemplate;
      var allowedViews = [];
      var permittedViewsFallbackList = [];

      if (
        dataSource === enumDataSource.serverchart ||
        dataSource === enumDataSource.plan ||
        dataSource === enumDataSource.pointintime
      ) {
        var myGroupID = wsMgr.GetUserGroupID();
        var myGroup = wsMgr.GetCurrentUserGroup();
        var myChartID = baseChartID;
        if (myGroupID == "0") allowedViews = templateList;
        else if (myGroup && myChartID)
          allowedViews = myGroup.GetAllowedViewsFromChartID(myChartID) ?? [];

        permittedViewsFallbackList = myGroup
          ? (myGroup.GetAllowedViewsFromChartID(myChartID) ?? templateList)
          : templateList;
        permittedViewsFallbackList = JSON.parse(JSON.stringify(permittedViewsFallbackList));
        permittedViewsFallbackList.push("Classic");

        // String-coerce: view IDs are not guaranteed to share a primitive type
        // across the client/server boundary (e.g. a custom view's GetTemplateID()
        // vs. the server-stored views/allowed lists), so a strict includes() can
        // spuriously reject an otherwise valid, promoted view.
        var currentTemplateID = String(currentTemplate);
        var isCurrentAllowed = allowedViews.map(String).includes(currentTemplateID);

        if (!templateList.map(String).includes(currentTemplateID) || !isCurrentAllowed) {
          //We don't currently have a template, or our current template is invalid.
          currentTemplate = permittedViewsFallbackList.shift();
        }
        //Set this elsewhere
      } else if (defaultTemplate != null) {
        currentTemplate = defaultTemplate;
      } else if (currentTemplate == null) {
        currentTemplate = templateList[0]; // fall back to basic
      }

      iWizData.SetChartTemplateName(currentTemplate);
      iWizData.SetChartTemplateSet(enumChartTemplateSet.sharedtemplates);

      var scm = this.GetSubChartMgr();
      if (scSort) scm.SetSubChartSort(scSort);

      var nameusing = scRule["nameusing"];

      if (metadata["headers"] && !metadata["headers"].includes(nameusing)) {
        //The ID i want to name my subcharts on is not in my headers
        if (metadata["fields"][nameusing]) nameusing = metadata["fields"][nameusing];
        else if (metadata["fields"]["Title"]) nameusing = "Title";
      }

      if (metadata["chainLevels"] > 0) {
        var scBreaks = parseInt(scRule["startbreaksat"]);
        scRule["startbreaksat"] = (parseInt(metadata["chainLevels"]) + scBreaks).toString();
      }

      var pageDensity = scRule["pagedensity"] != null ? parseInt(scRule["pagedensity"]) : 0;
      var breakOption = scRule["scChartBreakType"] != null ? scRule["scChartBreakType"] : 1;
      var breaklevels = scRule["breakevery"] + " Levels";
      var smartbreak = scRule["minsubsforbreak"];
      var breakField = scRule["breakfield"] ? scRule["breakfield"] : "";

      if (smartbreak === 1) smartbreak += " Box";
      else smartbreak += " Boxes";

      var smartstart = "Level " + scRule["startbreaksat"];

      iWizData.SetSubChartOption(breakOption);
      iWizData.SetSubChartNameFieldID(nameusing);
      iWizData.SetBreakLevels(breaklevels);
      iWizData.SetSmartBreak(smartbreak);
      iWizData.SetBreakField(breakField);
      iWizData.SetSmartStartLevel(smartstart);
      iWizData.SetAssistWizMode(enumAssistOption.byfield);
      iWizData.SetPageDensity(pageDensity);
      if (dataSource == enumDataSource.pointintime) iWizData.SetArchiveTimestamp(archiveDate);

      var masterfile;

      if (docType === "publish" && options["pubmaster"] && options["pubmaster"] !== "None") {
        masterfile = metadata["pubmaster"];
      }
      // from builder settings
      else if (metadata["mtemplate"]) {
        masterfile = metadata["mtemplate"];
      }
      // chart default
      else {
        masterfile = metadata["mastertemplate"];
        iWizData.SetDefaultBackground(masterfile);
      }

      if (masterfile && masterfile != "None") {
        // todo update to be compatible with new .ocb background file ext - this actually may not be necessary anymore
        //TODO: Save with extension if it will be .ocb.
        var extensionChecker = masterfile.split(".").pop();
        if (extensionChecker !== "oc5m" && extensionChecker !== "ocb")
          masterfile = masterfile + ".oc5m";
      } else masterfile = priorDocMetadata?.GetMasterFile();

      var mpMode = masterfile != null ? enumBGOption.linked : enumBGOption.custom;
      iWizData.SetMasterFile(masterfile);
      iWizData.SetMasterFolder("Masters");
      iWizData.SetMasterOption(mpMode);

      var importFilter = metadata["importFilter"];
      if (importFilter) {
        iWizData.SetFilterData(true);
        iWizData.SetFilterRule(importFilter["rule"]);
      }

      var sortOption = metadata["sort"];
      if (sortOption) {
        iWizData.SetSortChart(true);
        var sortConfig = new QSortConfig().LoadFromJSON(sortOption);
        iWizData.SetSortCriteria(sortConfig);
      }

      if (metadata && metadata["name"] && docType !== "builder") {
        if (wsMgr && iWizData?.GetDataSource() === enumDataSource.pointintime) {
          docMgr.SetFileName(iWizData.GetArchiveString(wsMgr));
        } else {
          docMgr.SetFileName(metadata["name"]);
        }
      }

      var topBoxID = metadata["topBoxID"];
      if (topBoxID) {
        iWizData.SetSpecifyTopBox(true);
        //Array structure to be cross compatible with coheads.
        iWizData.SetTopBoxIDs([topBoxID]);
      }

      const forceTopBoxID = metadata["forceTopBoxID"];
      if (forceTopBoxID) {
        iWizData.SetForceTopBoxID([forceTopBoxID]);
      }

      var description = metadata["description"];
      if (dataSource == enumDataSource.serverchart && description) {
        iWizData.SetDescription(description);
      }
      var boxCount = metadata["boxcount"];
      if (boxCount) {
        iWizData.SetBaseBoxCount(boxCount);
      }
      var orphan = metadata["orphans"];
      if (orphan) {
        iWizData.SetOrphans(orphan);
      }

      /*
        if (chartData['placeHolders'])
        {
            var placeHolderParams = chartData['placeHolders'];
            iWizData.SetPlaceHolder(true);

            if (placeHolderParams['placeHolderFunction'])
                iWizData.SetPlaceHolderField(placeHolderParams['placeHolderFunction']);

            if (placeHolderParams['placeHolderSupFunction'])
                iWizData.SetPlaceHolderField2(placeHolderParams['placeHolderSupFunction']);
        }
        Replaced with master chart version
         */

      var fieldSourceMaps = CreateFieldSourceMaps(metadata);
      iWizData.SetFieldSourceMaps(fieldSourceMaps);
    }

    var importConfig = metadata["import"];
    if (importConfig) {
      iWizData.SetImport(importConfig);
    }

    let activeView = iWizData.GetChartTemplateID();
    var theTemplate = docMgr.fWSMgr
      .GetChartTemplatesMgr()
      .FindTemplateByIDAllSets(activeView, false);
    if (theTemplate["success"]) {
      var curDoc = this.GetCurrentDoc();
      curDoc.SetActiveTemplate(theTemplate["template"]);
    }

    if (!priorDocMetadata) {
      var doCoHeads =
        metadata.doCoHead &&
        metadata.topBoxID &&
        metadata.coHeads?.length > 0 &&
        metadata.coHeads.some(cohead => cohead.PersonID === metadata.topBoxID);
      if (doCoHeads) {
        iWizData.SetCoHeadChart(true);
        iWizData.SetSpecifyTopBox(true);
        var topboxids = metadata["coHeads"].map(function (a) {
          return a["PersonID"];
        });
        iWizData.SetTopBoxIDs(topboxids);
      } else {
        iWizData.SetCoHeadChart(false);
      }
    }

    /*
    var multiChartOption = options['multiChartInfo'];
    if (multiChartOption)
    {
        var multiChartParams = docMgr.GetCompoundParams();
        multiChartParams.SetCompoundDoc(multiChartOption['doMultiChart']);
        multiChartParams.SetCompoundFilterID(multiChartOption['multiChartSplitOn']);
        multiChartParams.SetAltFilterID(multiChartOption['multiChartAltSplitOn']);
        multiChartParams.fdoSubDivide = true;       //Treat the alt split key as a subdivide instead of fallback.
        multiChartParams.fv2PreProcessor = true;    //Check for duplicates.
    }
     */

    iWizData.fAutoSyncSchedule = metadata["sync"];
    iWizData.fMapping = metadata["fields"];
    if (IsPositionChart(metadata)) {
      iWizData.fPositionConfig = new PositionConfig(metadata);
    }

    docMgr.fChartMetadata = iWizData.Clone(); // this gets done automatically at the end
    docMgr.fIWizDataRefresh = iWizData.Clone(); // this gets done automatically at the end
  }

  OptimizeAssistants() {
    var iwr = this.fIWizDataRefresh;
    if (iwr == null) return true;

    return !iwr.GetPersistAssist();
  }

  GetStartAssistLeft() {
    var iwr = this.fIWizDataRefresh;
    if (iwr == null) return false;

    return iwr.GetStartAssistLeft();
  }

  AllowOptimizeSort() {
    var iwr = this.fIWizDataRefresh;
    if (iwr == null) return true;

    var persistSort = iwr.GetPersistBoxSort();
    var allowSort = !persistSort;
    return allowSort;
  }

  GetDocPrefs() {
    //, importSort)
    return this.fDocPrefs;
  }

  SetupDocPrefs(
    newDocPrefs //, importSort)
  ) {
    var docPrefs = this.GetDocPrefs();
    docPrefs.SetupDocPrefs(newDocPrefs);
  }

  GetViewMgr() {
    return this.fViewMgr;
  }

  GetProfileMgr() {
    return this.fProfileMgr;
  }

  GetProfileList(className = enumObjClassNames.box) {
    var pm = this.GetProfileMgr();
    return pm.GetProfileList(className);
  }

  CopyViews(priorDocMgr) {
    //   if (priorDoc == null)
    //       return;

    var oldViewMgr = priorDocMgr.GetViewMgr();
    var curViewMgr = this.GetViewMgr();

    curViewMgr.CopyViews(oldViewMgr);
  }

  CopyCompoundParams(priorDocMgr) {
    var params = priorDocMgr.GetCompoundParams();
    this.UpdateCompoundParams(params);
  }

  GetSubChartMgr() {
    var curDoc = this.GetCurrentDoc();
    return curDoc.GetSubChartMgr();
  }

  UnRegisterDoc(aDoc) {
    var index = this.fDocList.indexOf(aDoc);
    if (index != -1) this.fDocList.splice(index, 1);
  }

  RegisterDoc(aDoc) {
    this.fDocList.push(aDoc);
  }

  GetCurrentDoc() {
    return this.fCurDoc;
  }

  SetCurrentDoc(aDoc) {
    this.fCurDoc = aDoc;
    // if (aDoc)
    //     aDoc.ClearFormulaCache();
  }

  GetNumDocuments() {
    return this.fDocList.length;
  }

  GetIndexDoc(index) {
    return this.fDocList[index];
  }

  GetDocByDivName(lookupDiv) {
    var n = this.GetNumDocuments();
    for (var i = 0; i < n; i++) {
      var d = this.GetIndexDoc(i);
      var divName = d.GetDivisionName();
      if (divName == lookupDiv) return d;
    }

    return null;
  }

  GetDivisionNameList() {
    var theList = [];
    var n = this.GetNumDocuments();
    for (var i = 0; i < n; i++) {
      var d = this.GetIndexDoc(i);
      var divName = d.GetDivisionName();
      theList.push(divName);
    }

    var curDoc = this.GetCurrentDoc();
    if (curDoc) {
      var objDB = curDoc.GetObjDB();
      var baseClass = objDB.GetClassByName("Box");
      /*
        var fieldDef = baseClass.GetFieldDefByID("divMap");

        if (fieldDef != null) {
            baseClass.GetFieldDefByID("divMap")
        }
        else*/
      //theList.sort(function(a,b){return (a > b) ? 1 : -1});
    }
    return theList;
  }

  GetChartDocAuthor() {
    // Note: old oc5 chart docs were improperly saving "none" to all chart docs (bad logic). Updating this only for new chart docs
    var author = this.fAuthor;
    if (author == "none") author = this.fWSMgr.GetUserName();
    return author;
  }

  OutputToJSON(json) {
    var backgroundSave = this.GetSaveAsMPTemplate();
    var versionNum = kMultiDocVersion;
    json["version"] = versionNum;
    json["author"] = this.GetChartDocAuthor();

    if (!backgroundSave) {
      json["refreshdate"] = this.fRefreshDate;
      json["refreshtime"] = this.fRefreshTime;
      // this.fProfileMgr.OutputToJSON(json);
      this.fViewMgr.OutputToJSON(json);
    }

    this.fDocPrefs.OutputToJSON(json);

    if (this.fChartMetadata != null) {
      if (backgroundSave) {
        json["chartMetadata"] = this.fChartMetadata.OutputToJSONBackground();
      } else {
        json["chartMetadata"] = this.fChartMetadata.OutputToJSON();
      }
    }

    var curDoc = this.fCurDocSave;
    json["docs"] = [];

    var pm = this.GetPageMgr();
    json["pageMgr"] = {};
    pm.OutputToJSON(json["pageMgr"]);

    var backgroundMgr = this.fWSMgr.GetBGMgr();
    if (backgroundMgr.fActiveBackgroundArea) {
      json["backgroundMgr"] = backgroundMgr.OutputToJSON();
    }

    if (this.fCurDocSave == null) {
      var curDoc = this.GetCurrentDoc();
      var curDivName = curDoc.GetDivisionName();
      this.SetCurrentDiv(curDivName);

      this.fCompoundParams.OutputToJSON(json);
      var n = this.GetNumDocuments();
      for (var i = 0; i < n; i++) {
        var d = this.GetIndexDoc(i);
        d.OutputToJSON(json["docs"]);
      }
    } else curDoc.OutputToJSON(json["docs"]);
  }

  OutputSubElements(stream) {
    var versionNum = kMultiDocVersion;
    this.OutputSimpleElement("version", versionNum, stream);
    this.OutputSimpleElement("refreshdate", this.fRefreshDate, stream);
    this.OutputSimpleElement("refreshtime", this.fRefreshTime, stream);
  }

  OutputChildElements(stream) {
    this.fProfileMgr.OutputToXML(stream);
    this.fViewMgr.OutputToXML(stream);
    this.fDocPrefs.OutputToXML(stream);

    var aM = this.GetAccessMgr();
    var isAdmin = aM.IsAdmin();
    if (isAdmin) {
      var mpT = this.GetSaveAsMPTemplate();
      if (!mpT) this.fAccessMgr.OutputToXML(stream);
    } else {
      // A R/W User cannot refresh a restricted chart after saving
      if (aM.GetRestrictAccess()) {
        this.fIWizDataRefresh = null;
        if (this.fChartMetadata != null) this.fChartMetadata.SetDataSource(enumDataSource.manual);
      }
    }

    if (this.fIWizDataRefresh != null) {
      this.fIWizDataRefresh.SetXMLElementName("refreshparams");
      this.fIWizDataRefresh.OutputToXML(stream);
    }

    if (this.fChartMetadata != null) {
      this.fChartMetadata.SetXMLElementName("wizparams");
      this.fChartMetadata.OutputToXML(stream);
    }

    var curDoc = this.fCurDocSave;
    if (this.fCurDocSave == null) {
      var curDoc = this.GetCurrentDoc();
      var curDivName = curDoc.GetDivisionName();
      this.SetCurrentDiv(curDivName);

      this.fCompoundParams.OutputToXML(stream);
      var n = this.GetNumDocuments();
      for (var i = 0; i < n; i++) {
        var d = this.GetIndexDoc(i);
        d.OutputToXML(stream);
      }
    } else curDoc.OutputToXML(stream);

    var pm = this.GetPageMgr();
    pm.OutputToXML(stream);
  }

  ConvertLegacyDocNameToDocV2(fileName) {
    var ext = GetExtension(fileName);
    if (ext == "oc5m" || ext == "oc5") {
      if (ext == "oc5m") fileName = fileName.replace("oc5m", "ocb");
      if (ext == "oc5") fileName = fileName.replace("oc5", "ocd");
    }
    return fileName;
  }

  SaveDocument(orgName, fileName, directory, saveScope) {
    var w = this.fWSMgr;
    var curDoc = w.GetDoc();
    w.SyncBackgroundWithDoc(curDoc, null);

    var clearFilter = false;
    this.fCurDocSave = null;

    var masterFile = this.IsBackground(fileName);
    this.SetSaveAsMPTemplate(masterFile);
    if (masterFile) {
      w.fBackgroundMgr.BustMasterCache(fileName);
    }

    var saveCurDocOnly = masterFile;

    if (saveScope == null) saveScope = enumSaveScope.entireChart;
    else if (saveScope == enumSaveScope.currentBranch) {
      curDoc.SetFilterToBranch();
      clearFilter = true;
      saveCurDocOnly = true;
    } else if (saveScope == enumSaveScope.currentPage) {
      clearFilter = true;
      saveCurDocOnly = true;
    }

    if (saveCurDocOnly) {
      this.fCurDocSave = curDoc;
      curDoc.SetSaveScope(saveScope);
    } else this.SetSaveScope();

    var stream = {};
    this.OutputToJSON(stream);

    var loadType = Globals._accountInfo.fReportToWebDriver ? -1 : 0;

    var jsonData = {};
    jsonData["org"] = orgName;
    jsonData["user"] = w.GetUserName();
    jsonData["filename"] = fileName;
    jsonData["directory"] = directory;
    jsonData["thedata"] = stream;
    jsonData["loadtype"] = loadType;

    var j = JSON.stringify(jsonData);

    //var d2 = new Date();
    //var s2 = d2.getTime();
    //var elapsed = s2 - s1;
    //console.log("Save [1B] time: " + elapsed);

    if (clearFilter) curDoc.ClearBranchFilter();

    return j; // stream;
  }

  SetSaveScope() {
    var n = this.GetNumDocuments();
    for (var i = 0; i < n; i++) {
      var d = this.GetIndexDoc(i);
      d.SetSaveScope(enumSaveScope.entireChart);
    }
  }

  IsBackground(fileName) {
    var f = fileName;

    if (f == null) f = this.GetFileName(true);
    if (f) {
      var ext = GetExtension(f);
      if (ext == "oc5m" || ext == "ocb") return true;
    }

    return false;
  }

  StampRefreshDate() {
    // Sets refresh time and refresh date value to now
    var aDate = new Date();
    var d = GetStdDate(aDate);
    var hours = aDate.getHours().toString().padStart(2, "0");
    var minutes = aDate.getMinutes().toString().padStart(2, "0");
    this.SetRefreshDate(d);
    this.SetRefreshTime(hours + minutes);
  }

  SetRefreshDate(refreshDate) {
    this.fRefreshDate = refreshDate;
  }

  GetRefreshDate() {
    return this.fRefreshDate;
  }

  SetRefreshTime(refreshTime) {
    this.fRefreshTime = refreshTime;
  }

  GetRefreshTime() {
    return this.fRefreshTime;
  }

  SetFileName(fileName) {
    this.fFileName = fileName;
  }

  GetFileName(fullName) {
    var openDoc = this.GetCurrentDoc();
    if (fullName || openDoc?.IsServerChart()) return this.fFileName;
    else return StripExtension(this.fFileName);
  }

  LoadDocAsJSON(filename) {
    var checkFile = filename ? filename : this.GetFileName(true);
    return SaveLoadFileAsJSON(checkFile);
  }

  LoadDocAsXML() {
    return LoadFileAsXML(this.GetFileName(true));
  }

  IsAutoSaveEnabled() {
    var iWizData = this.GetIWizDataInteractive();
    return iWizData.fIsAutoSave;
  }

  GetFormattedChartName(showDir = false, showUnsaved = true) {
    var unSaved = this.fWSMgr.ReadyToSave();
    var dirName = this.GetFileDirectory();
    var openDoc = this.GetCurrentDoc();
    var isServerChart = openDoc?.IsServerChart();
    var fileName = this.GetFileName(showDir);
    if (!fileName) fileName = appstr("Untitled");
    if (isServerChart) return fileName;
    if (showUnsaved && unSaved && !this.IsAutoSaveEnabled()) fileName = "*" + fileName;
    return (showDir && dirName ? dirName + " \\ " : "") + fileName;
  }

  SetFileDirectory(directoryName, dirLocked) {
    this.fDirectoryName = directoryName;
    this.fDirLocked = dirLocked;
  }

  GetFileDirectory() {
    return this.fDirectoryName;
  }

  GetDirLocked() {
    return this.fDirLocked;
  }

  InitAccessMgr() {
    // var curUser = this.fWSMgr.GetUserName();
    // var ut = Globals._accountInfo.GetUserRole();
    // var isAdmin = (ut == kAdminUser)
    this.fAccessMgr = new QAccessMgr();
    this.fAccessMgr.InitAccessMgr(); //curUser, isAdmin);
    // this.fAccessMgr.LoadPrivs();
  }

  SetupAccessMgr(priorAccessMgr) {
    var curUser = this.fWSMgr.GetUserName();
    var ut = Globals._accountInfo.GetUserRole();
    var isAdmin = ut == kAdminUser;
    var accessM = this.GetAccessMgr();
    if (priorAccessMgr) accessM.CopyPrior(priorAccessMgr);
    accessM.SetupAccessMgr(curUser, isAdmin);
  }

  // Load Methods
  LoadFromJSON(JSON) {
    this.fDocMgrVersion = JSON["version"];
    this.fRefreshDate = JSON["refreshdate"] ?? "";
    this.fRefreshTime = JSON["refreshtime"] ?? "";
    this.fAuthor = JSON["author"];

    // if (JSON["profile"]) this.fProfileMgr.LoadFromJSON(JSON["profile"]);
    // if (JSON["views"]) this.fViewMgr.LoadFromJSON(JSON["views"]);

    this.fDocPrefs.LoadFromJSON(JSON["docPreferences"]);
    this.fPageMgr.LoadFromJSON(JSON["pageMgr"]);

    if (JSON["iWizRefresh"]) {
      this.fIWizDataRefresh = new QIWizData();
      this.fIWizDataRefresh.LoadFromJSON(JSON["iWizRefresh"]);
      this.fIWizDataRefresh.SetUpdateMode(enumIWizDataSet.refresh);
    }

    if (JSON["chartMetadata"]) {
      this.fChartMetadata = new QIWizData();
      this.fChartMetadata.LoadFromJSON(JSON["chartMetadata"]);
      this.fChartMetadata.SetUpdateMode(enumIWizDataSet.interactive);
    }

    var docList = JSON["docs"];
    for (var i = 0; i < docList.length; i++) {
      var d = this.LoadDocument(docList[i], true, true);
      if (d) this.SetSCNameField(d);
    }

    var compoundParams = JSON["multiParams"];
    this.fCompoundParams = new QMultiDocParams();
    if (compoundParams != null) this.fCompoundParams.LoadFromJSON(compoundParams);

    if (JSON["backgroundMgr"]) {
      var wsMgr = this.fWSMgr;
      var activeBackground = wsMgr.fBackgroundMgr.LoadFromJSON(JSON["backgroundMgr"]);
      wsMgr.fBackgroundMgr.SetActiveBackground(activeBackground, this.GetFileName(true));
    }
  }

  OpenDocument(lastSave) {
    //  var e1 = StartTime();

    var loader = new QXMLLoader();
    var objTree = loader.LoadXML(lastSave);
    //   var e2 = ElaspedTime(e1,"XML Load:");

    //this.SetupAccessMgr();
    var rootElementName = objTree.GetElementName();
    if (rootElementName == "qxdocmgr") this.LoadFromXML(objTree);
    else {
      this.fDocMgrVersion = kMultiDocVersion;
      var d = this.LoadDocument(objTree, false);
      this.SetSCNameField(d);
    }

    this.ResetRefMgr();
  }

  LoadSubElements(objTree) {
    this.fDocMgrVersion = objTree.GetValueForChild("version", true);
    this.fRefreshDate = objTree.GetValueForChild("refreshdate", false);
    this.fRefreshTime = objTree.GetValueForChild("refreshtime", false);
  }

  AccessMgrEnabled() {
    return this.fAccessMgr.AccessMgrEnabled();
  }

  LoadDocument(objTree, applyAccessMgr, doJSON) {
    var w = this.fWSMgr;

    var d = w.CreateDocument(false, false, false, false, this); // todo: revisit holders class for doc load
    doJSON ? d.LoadFromJSON(objTree) : d.LoadFromXML(objTree);

    var excludeList = [];

    var unRegister = false;
    var aM = this.GetAccessMgr();
    if (applyAccessMgr && this.AccessMgrEnabled()) {
      var curDiv = d.GetDivisionName();
      var includeDiv = aM.IncludeDivision(curDiv);
      if (includeDiv == false) unRegister = true;
      else {
        var baseDB = d.GetObjDB();
        var newDB = this.ApplyDeptFilter(baseDB, excludeList);
        if (newDB == null) unRegister = true;
        else d.ReplaceObjDB(newDB);
      }
      if (unRegister) {
        this.UnRegisterDoc(d);
        return null;
      }
    }

    if (excludeList.length == 0) excludeList = null;

    var canvasRef = d.GetCanvasRef();
    this.SetCurrentDoc(d);

    if (doJSON) {
      var plmData = objTree["pageLayoutMgr"];
      if (plmData) d.fPageLayoutMgr.LoadFromJSON(plmData);
    } else {
      var plmData = objTree.GetChildByName("pagelayoutmgr");
      if (plmData) d.fPageLayoutMgr.LoadFromXML(plmData);
    }

    var noLoadSubCharts =
      this.IsServerChart() ||
      this.GetIWizDataInteractive().fIsLiveChart ||
      IsBackgroundFile(this.GetFileName(true));
    if (!noLoadSubCharts) canvasRef.LoadSubCharts(d, excludeList);
    else if (!Globals._uiConfig.InBackgroundEditorMode()) {
      w.ClearDirty();
    }

    return d;
  }

  LoadDocumentData(data) {
    var useJSONLoad = this.LoadDocAsJSON();
    if (useJSONLoad) {
      var theData = JSON.parse(data);
      this.LoadFromJSON(theData);
    } else {
      var s = new QStream();
      s.InitStreamFromXML(data);
      this.OpenDocument(s);
    }
  }

  GetFirstDocument(targetDiv) {
    var firstDoc = null;
    if (targetDiv != null) firstDoc = this.GetDocByDivName(targetDiv);

    if (firstDoc == null) {
      var curDiv = this.GetCurrentDiv();
      if (curDiv) firstDoc = this.GetDocByDivName(curDiv);
    }

    if (firstDoc == null) firstDoc = this.GetIndexDoc(0);
    return firstDoc;
  }

  HandleDocumentLoad(loadDocument, settings = {}) {
    var documentName = this.GetFileName(true);
    var backgroundLoad = this.IsBackground(documentName);
    var liveLoad = this.IsServerChart() || this.IsLiveChart();

    if (liveLoad) this.DoLiveLoad(loadDocument, settings);
    else if (backgroundLoad) this.DoBackgroundLoad(loadDocument);
    else this.DoStandardLoad(loadDocument, settings);
  }

  DoStandardLoad(loadDocument, settings = {}) {
    var w = this.fWSMgr;
    loadDocument.LoadBaseChartMetadata();
    w.NavigateToTopBox(settings.topboxID);
    loadDocument.ApplyChartFilters();
    loadDocument.ApplyChartHighlights();
    this.DoBackgroundLoad(loadDocument);
    w.BroadcastTopic("chartDocumentLoaded", loadDocument);
  }

  DoLiveLoad(loadDocument, settings = {}) {
    var iWiz = loadDocument.GetIWizData();
    iWiz.SetPriorDocMgr(this);
    loadDocument.LoadBaseChartMetadata();
    loadDocument.DoChartDataQuery(settings.topboxID);
  }

  DoBackgroundLoad(loadDocument) {
    var documentName = this.GetFileName(true);
    var w = this.fWSMgr;
    var bgMgr = w.fBackgroundMgr;
    var iWiz = loadDocument.GetIWizData();
    var pm = loadDocument.GetPageMgr();

    if (iWiz.GetMasterOption() !== enumBGOption.linked) {
      var pageProps = pm.GetPageProps();
      var scm = loadDocument.GetSubChartMgr();
      var loadFromNodes = !bgMgr.fActiveBackgroundArea;
      var nodeList = loadFromNodes ? scm.GetNodesForSubChart(kMasterSCID, false) : null;
      bgMgr.LoadActiveBackground(documentName, nodeList, pageProps, loadFromNodes);
    }
    w.ApplyBackground();
  }

  LoadOldProfile(objTree) {
    this.fProfileMgr.LoadFromXML(objTree);
  }

  LoadOldDocPrefs(objTree) {
    this.fDocPrefs.LoadFromXML(objTree);
  }

  LoadOldViewPrefs(objTree) {
    this.fViewMgr.LoadFromXML(objTree);
  }

  LoadOldPageMgr(objTree, theDoc) {
    var pm = this.GetPageMgr();
    pm.LoadFromXML(objTree);

    var scm = theDoc.GetSubChartMgr();
    var subChartMgrData = objTree.GetChildByName("subchartmgr");
    if (subChartMgrData != null) scm.LoadFromXML(subChartMgrData);
    else scm.CreateRootSubChart();
  }

  LoadWizData(refreshData, wizparams) {
    if (refreshData != null) {
      this.fIWizDataRefresh = new QIWizData();
      //TODO: The below line would normally be taken care of by the init call, but it was omitted here in the prior code path and I don't want to disrupt this load process
      //if it depended on init NOT being called.
      this.fIWizDataRefresh.fMapping = {};
      this.fIWizDataRefresh.LoadFromXML(refreshData);
      this.fIWizDataRefresh.SetUpdateMode(enumIWizDataSet.refresh);
    }

    if (wizparams != null) {
      this.fChartMetadata = new QIWizData();
      this.fChartMetadata.fMapping = {};
      this.fChartMetadata.LoadFromXML(wizparams);
      this.fChartMetadata.SetUpdateMode(enumIWizDataSet.interactive);

      //    var namingSCID = this.fChartMetadata.GetSubChartNameFieldID();
      //    var scm = this.GetSubChartMgr();
      //    scm.SetNameUsingID(namingSCID);
    }
  }

  SetLegacyBoxLayoutList(layoutList) {
    this.fLegacyBoxLayoutList = layoutList;
  }

  GetLegacyBoxLayoutList(clearIt) {
    var a = this.fLegacyBoxLayoutList;
    this.fLegacyBoxLayoutList = null;

    return a;
  }

  SetSCNameField(aDoc) {
    var iWiz = this.GetIWizDataInteractive();
    if (iWiz) {
      var namingSCID = iWiz.GetSubChartNameFieldID();
      var scm = aDoc.GetSubChartMgr();
      scm.SetNameUsingID(namingSCID);
    }
  }

  LoadChildElements(objTree) {
    // PageMgr must be loaded before documents
    var pageMgrData = objTree.GetChildByName("pagemgr");
    if (pageMgrData) this.fPageMgr.LoadFromXML(pageMgrData);

    var refreshData = objTree.GetChildByName("refreshparams");
    var wizparams = objTree.GetChildByName("wizparams");
    this.LoadWizData(refreshData, wizparams);

    // AccessMgr must be loaded before documents
    var accessMgr = objTree.GetChildByName("accessmgr");
    if (accessMgr != null) this.fAccessMgr.LoadFromXML(accessMgr);
    this.SetupAccessMgr(null);

    var childList = objTree.GetChildOfTypeList("qxdoc");
    var n = childList.length;
    for (var i = 0; i < n; i++) {
      var d = this.LoadDocument(childList[i], true);
      if (d) this.SetSCNameField(d);
    }

    var compoundParams = objTree.GetChildByName("multidoc");
    this.fCompoundParams = new QMultiDocParams();
    if (compoundParams != null) this.fCompoundParams.LoadFromXML(compoundParams);

    var docPrefs = objTree.GetChildByName("docprefs");
    this.fDocPrefs.LoadFromXML(docPrefs);

    var viewMgr = objTree.GetChildByName("viewmgr");
    if (viewMgr) this.fViewMgr.LoadFromXML(viewMgr);

    var profileMgr = objTree.GetChildByName("profilemgr");
    if (profileMgr) this.fProfileMgr.LoadFromXML(profileMgr);
  }

  IsDocDirty() {
    return this.fDirty;
  }

  SetDirty(isDirty) {
    this.fDirty = isDirty;
    if (isDirty && this.IsAutoSaveEnabled()) {
      if (this.fSaveQueueId != null) {
        clearTimeout(this.fSaveQueueId);
      }
      if (this.fSaveInProgress) {
        this.fSaveQueueId = setTimeout(() => this.SetDirty(isDirty), 2000);
      } else {
        this.fWSMgr.AutoSave();
      }
    }
  }

  HasBackgroundAttached() {
    let iWizData = null;
    var wsMgr = this.fWSMgr;
    var docMgr = wsMgr?.GetDocMgr();
    if (docMgr) iWizData = docMgr.GetIWizDataInteractive();
    var mpOption = iWizData?.GetMasterOption();
    var customFile = iWizData?.GetMasterFile();
    return (customFile && customFile.length > 0) || mpOption === enumBGOption.linked;
  }

  GetCompoundParams() {
    return this.fCompoundParams;
  }

  GetBackgroundSaveDict() {
    var iWizData = this.GetIWizDataInteractive();
    if (!iWizData) return { file: appstr("Untitled"), dir: "Masters" };
    var customFile = iWizData.GetMasterFile();
    var customDir = iWizData.GetMasterFolder();
    if (customDir == null) customDir = "Masters";
    if (customFile == null) {
      customFile = "";
    }
    return { file: customFile, dir: customDir };
  }

  GetCompoundDoc() {
    return this.fCompoundParams.GetCompoundDoc();
  }

  SetCurrentDiv(currentDiv) {
    this.fCompoundParams.SetCurrentDiv(currentDiv);
  }

  GetCurrentDiv() {
    return this.fCompoundParams.GetCurrentDiv();
  }

  GetCompoundFilterID() {
    return this.fCompoundParams.GetCompoundFilterID();
  }

  GetAltFilterID() {
    return this.fCompoundParams.GetAltFilterID();
  }

  GetChain() {
    return this.fCompoundParams.GetChain();
  }

  GetAddExec() {
    return this.fCompoundParams.GetAddExec();
  }

  GetAutoFixHierarchy() {
    return this.fCompoundParams.GetAutoFixHierarchy();
  }

  ValidCompoundOptions() {
    var compoundDoc = this.GetCompoundDoc();
    if (compoundDoc) {
      var filterID = this.GetCompoundFilterID();
      if (filterID == null) return false;
    }

    return true;
  }

  UpdateCompoundParams(params) {
    this.fCompoundParams = params.Clone();
  }

  GetDivisionList() {
    //simpleDoc
    var divisions = [];

    var baseDoc = this.GetIndexDoc(0);
    var baseDB = baseDoc.GetObjDB();
    var compoundInfo = this.GetCompoundParams();
    var filterFieldID = compoundInfo.GetCompoundFilterID();

    var baseClass = baseDB.GetClassByName("Box");
    var fieldDef = baseClass.GetFieldDefByID(filterFieldID);
    if (!fieldDef) {
      fieldDef = baseClass.GetFieldDefByName(filterFieldID);
      if (fieldDef) compoundInfo.SetCompoundFilterID(fieldDef.GetFieldID());
    }
    if (!fieldDef) {
      divisions.push("*");
      this.fRootDiv = "*";
      return divisions;
    }
    var objCount = baseClass.GetObjectCount();
    var sortingFieldID = baseClass.GetFieldDefByID("DivMap");
    var rootObj = baseDoc.GetLargestTree(baseClass, false, false, null);
    this.fRootDiv = null;

    for (var i = 0; i < objCount; i++) {
      var anObj = baseClass.GetIndexObject(i);
      if (anObj.IsPersistent()) {
        var v = anObj.GetFieldValue(fieldDef);
        if (v == null || v == "") {
          v = "<Blank>";
          anObj.SetFieldValue(fieldDef, v);
        }
        var s = sortingFieldID != null ? anObj.GetFieldValue(sortingFieldID) : v;

        if (compoundInfo.fdoSubDivide && compoundInfo.GetAltFilterID()) {
          var altFilterDefID = compoundInfo.GetAltFilterID();
          var altFilterDef = baseClass.GetFieldDefByID(altFilterDefID);
          var v2 = anObj.GetFieldValue(altFilterDef);
          if (v2) {
            v += "\n" + v2;
          }
        }

        if (anObj == rootObj) this.fRootDiv = v;

        divisions.push({ name: v, rank: s });
      }
    }
    //First, sort divisions based off their sorting key tied to them (either alphabetic or integer- the comparison works either way)
    divisions.sort(function (a, b) {
      return a["rank"] > b["rank"] ? 1 : -1;
    });
    //Then reduce the array to a flat list of names
    divisions = divisions.map(function (value, index, array) {
      return value["name"];
    });
    //Then remove duplicates - since we could have a scenario with multiple different rank for the same name values.
    //This produces slightly indeterminate behavior with the sorting, but it's a data issue.
    divisions = divisions.filter(function (value, index, array) {
      return array.indexOf(value) === index;
    });
    return divisions;
  }

  AddParentsToFilter(
    parentList,
    addedParents,
    excludeList //, addChain)
  ) {
    var nextParentList = [];
    var pLen = parentList.length;
    if (pLen == 0) return nextParentList;

    for (var i = 0; i < pLen; i++) {
      var topObj = parentList[i];
      SetObjFilter(topObj, true, excludeList);
      //topObj["objfilter"] = "include";
      if (addedParents.indexOf(topObj) == -1) addedParents.push(topObj);
      MHAddParentToList(topObj, nextParentList);
    }

    nextParentList = RmvDupsParentList(nextParentList, false);
    var n = nextParentList.length;
    if (n > 0) nextParentList = this.AddParentsToFilter(nextParentList, addedParents, excludeList); //,addChain);

    return nextParentList;
  }

  CheckChain(anObj, addedParents, excludeList) {
    var childCount = anObj.GetNumChildren();
    var done = false;
    var k = 0;
    var count = 0;
    var nextTarget = null;

    while (!done && k < childCount) {
      var c = anObj.GetIndexChild(k);
      if (c["objfilter"] == "include") {
        count++;
        if (count == 1) nextTarget = c;
      }

      done = count > 1;
      k++;
    }

    if (count == 1) {
      SetObjFilter(anObj, false, excludeList);
      //anObj["objfilter"] = "exclude";
      var idx = addedParents.indexOf(nextTarget);
      if (idx != -1) this.CheckChain(nextTarget, addedParents, excludeList);
    }
  }

  HandleChain(addedParents, excludeList) {
    var chainLength = this.GetChain();
    if (chainLength > 0) return;

    var n = addedParents.length;
    if (n == 0) return;

    var roots = new Array();
    for (var i = 0; i < n; i++) {
      var p = addedParents[i];
      if (p.IsRoot()) roots.push(p);
    }

    var m = roots.length;
    for (var j = 0; j < m; j++) {
      var r = roots[j];
      this.CheckChain(r, addedParents, excludeList);
    }
  }

  FilterDB(baseDB, curDiv, isTopDoc) {
    var baseClass = baseDB.GetClassByName("Box");
    var compoundInfo = this.GetCompoundParams();
    var filterFieldID = compoundInfo.GetCompoundFilterID();
    var altFieldID = compoundInfo.GetAltFilterID();
    var fieldDef = baseClass.GetFieldDefByID(filterFieldID);
    //The below check is for when the assigned field def no longer exists. Should be extremely rare.
    if (!fieldDef || curDiv === "*") return;
    var altFieldDef = null;

    if (altFieldID) altFieldDef = baseClass.GetFieldDefByID(altFieldID);

    var objCount = baseClass.GetObjectCount();

    var subDivide = compoundInfo.fdoSubDivide && altFieldDef !== null;

    var parentList = [];
    var rootObj = null;
    var rootSubs = null;
    var keepList = [];
    // Set all members of the curDiv to "include"
    for (var i = 0; i < objCount; i++) {
      var anObj = baseClass.GetIndexObject(i);
      var v = anObj.GetFieldValue(fieldDef);
      var altV = null;
      //  var skipParentChain = false;
      if (altFieldDef) {
        altV = anObj.GetFieldValue(altFieldDef);
        //    if (altV != null && altV.length > 0)
        //        skipParentChain = true;
        // {
        // var parts = altV.split('|');
        // altV = parts[0];
        // if (parts.length = 2)
        //    altSuperID = parts[1];
        // }
      }
      var isAlt = altV != null && altV == curDiv;
      var keep = v == curDiv || isAlt;
      if (subDivide) {
        keep = curDiv == v + "\n" + altV;
      }

      if (keep) {
        SetObjFilter(anObj, true, null);
        MHAddParentToList(anObj, parentList);
        var isRoot = anObj.IsRoot();
        if (isRoot) {
          if (rootObj == null) {
            rootObj = anObj;
            rootSubs = rootObj.CountSubs();
          } else {
            var newSubs = anObj.CountSubs();
            if (newSubs > rootSubs) {
              rootObj = anObj;
              rootSubs = rootObj.CountSubs();
            }
          }
        }
        keepList.push(anObj);
      } else SetObjFilter(anObj, false, null);
    }

    var autoFix = this.GetAutoFixHierarchy();
    var addedParents = [];
    if (autoFix) {
      // We might have multiple heads for a dept
      var preProcessFix = compoundInfo.fv2PreProcessor;
      var parentList = RmvDupsParentList(parentList, preProcessFix);
      var parentList2 = this.AddParentsToFilter(parentList, addedParents, null); // ,false);
    }

    var addExec = this.GetAddExec();
    if (isTopDoc && addExec) this.IncludeChildren(rootObj);

    this.HandleChain(addedParents, null);
    keepList = keepList.concat(addedParents);
    return keepList;
  }

  IncludeChildren(rootObj) {
    if (rootObj == null) return;

    var n = rootObj.GetNumChildren();
    for (var i = 0; i < n; i++) {
      var child = rootObj.GetIndexChild(i);
      SetObjFilter(child, true, null);
      //  child["objfilter"] = "include";
    }
  }

  CopyFieldValues(curDoc, refreshInfo) {
    if (refreshInfo == null) return;

    var priorDocMgr = refreshInfo.GetPriorDocMgr();

    var divName = curDoc.GetDivisionName();
    var priorDoc = priorDocMgr.GetIndexDoc(0);
    if (divName != null) {
      priorDoc = priorDocMgr.GetDocByDivName(divName);
      if (priorDoc == null) {
        var isCompound = priorDocMgr.GetCompoundDoc();
        if (isCompound) return;
      }
    }
    var priorSource = priorDocMgr.GetIWizDataInteractive().GetDataSource();
    var isMasterChartSource = [
      enumDataSource.serverchart,
      enumDataSource.plan,
      enumDataSource.manualchart,
      enumDataSource.chartbuilder
    ].includes(priorSource);
    var fieldList = priorDoc.GetDBObjTypeInfo();
    var fieldDefPairs = curDoc.SetDBObjTypeInfo(fieldList, isMasterChartSource);
    curDoc.CopyFieldValues(fieldDefPairs, priorDoc);
  }

  ApplyDeptFilter(baseDB, excludeList) {
    var stream = new QStream();
    stream.InitStream();

    stream.Write("<compound>");
    baseDB.OutputToXML(stream);

    var aM = this.GetAccessMgr();
    var baseClass = baseDB.GetClassByName("Box");
    var objCount = baseClass.GetObjectCount();

    var deptFieldName = aM.GetDeptFieldName();
    var deptFieldDef = null;
    if (deptFieldName) deptFieldDef = baseClass.GetFieldDefByName(deptFieldName);

    var parentList = [];
    var rootObj = null;
    var rootSubs = null;
    // Set all members of the curDiv to "include"
    for (var i = 0; i < objCount; i++) {
      var anObj = baseClass.GetIndexObject(i);

      var deptName = null;
      if (deptFieldDef) deptName = anObj.GetFieldValue(deptFieldDef);

      var includeDept = aM.IncludeDept(deptName);
      if (includeDept) {
        SetObjFilter(anObj, true, excludeList);
        MHAddParentToList(anObj, parentList);
        var isRoot = anObj.IsRoot();
        if (isRoot) {
          if (rootObj == null) {
            rootObj = anObj;
            rootSubs = rootObj.CountSubs();
          } else {
            var newSubs = anObj.CountSubs();
            if (newSubs > rootSubs) {
              rootObj = anObj;
              rootSubs = rootObj.CountSubs();
            }
          }
        }
      } else SetObjFilter(anObj, false, excludeList);
      // anObj["objfilter"] = "exclude";
    }

    var addedParents = [];
    var parentList = RmvDupsParentList(parentList, false);
    var parentList2 = this.AddParentsToFilter(parentList, addedParents, excludeList); // ,false);
    this.HandleChain(addedParents, excludeList);
    baseDB.OutputObjects(stream);
    stream.Write("</compound>");

    var loader = new QXMLLoader();
    var objTree = loader.LoadXML(stream);

    var newDB = new QObjDB();
    newDB.InitDB();
    var objectSchema = objTree.GetChildByName("qobjdbschema");
    newDB.LoadFromXML(objectSchema);
    var objectData = objTree.GetChildByName("qObjectData");
    newDB.LoadObjects(objectData);

    var newBaseClass = newDB.GetClassByName("Box");
    objCount = newBaseClass.GetObjectCount();
    if (objCount == 0) return null;

    return newDB;
  }

  FinishImport(refreshInfo, docRef = null) {
    var isCompound = this.GetCompoundDoc();
    var w = this.fWSMgr;
    var baseDoc = docRef ?? this.GetIndexDoc(0);

    var priorDocMgr = null;
    if (refreshInfo) priorDocMgr = refreshInfo.GetPriorDocMgr();

    // var applyAccessMgr = this.AccessMgrEnabled();
    var simpleDoc = !isCompound; // && !applyAccessMgr;
    if (simpleDoc) {
      this.CopyFieldValues(baseDoc, refreshInfo);
      w.FinishDocSetup(baseDoc);
    } else {
      var baseDB = baseDoc.GetObjDB();
      //var baseClass = baseDB.GetClassByName("Box");

      // Find all unique values for the filter field
      var divisions = this.GetDivisionList(); //isCompound);
      var rootDiv = this.fRootDiv;

      var orgInfo = w.GetOrgInfo();
      // var iwizData = baseDoc.GetIWizData(enumIWizDataSet.refresh);
      //var dataSource = iwizData.GetDataSource();

      var initialDiv = null;
      // Create new docs based on list
      var n = divisions.length;
      for (var i = 0; i < n; i++) {
        var curDiv = divisions[i];
        var stream = new QStream();
        stream.InitStream();

        stream.Write("<compound>");
        baseDB.OutputToXML(stream);
        var baseClass = baseDB.GetClassByName("Box");
        var topDiv = false;
        if (curDiv == rootDiv) topDiv = true;
        var filteredObjList = this.FilterDB(baseDB, curDiv, topDiv);
        baseDB.OutputObjects(stream);
        stream.Write("</compound>");

        var loader = new QXMLLoader();
        var objTree = loader.LoadXML(stream);

        var newDB = new QObjDB();
        newDB.InitDB();
        var objectSchema = objTree.GetChildByName("qobjdbschema");
        newDB.LoadFromXML(objectSchema);
        var objectData = objTree.GetChildByName("qObjectData");
        newDB.LoadObjects(objectData);
        if (priorDocMgr && priorDocMgr.fDocList != null) {
          var priorDocDiv = priorDocMgr.GetDocByDivName(curDiv);
          if (priorDocDiv) {
            var oldDB = priorDocDiv.GetObjDB();
            newDB.CopyFilterState(oldDB);
            newDB.CopyHighlightState(oldDB);
          }
        }
        var newDoc;
        if (i > -1) {
          newDoc = new QXDoc();
          newDoc.InitXDoc(false, false, enumHierarchyTypes.employees, this, newDB, false, w);
          this.RegisterDoc(newDoc);
          newDoc.SetDivisionName(curDiv);
          if (baseDoc.GetActiveTemplate()) newDoc.SetActiveTemplate(baseDoc.GetActiveTemplate());
          w.CreateCanvasPanel(newDoc);
          w.SwitchToDoc(newDoc, false);
        }
        if (i == 0) {
          //baseDoc.ReplaceObjDB(newDB);
          //baseDoc.SetDivisionName(curDiv);
          //newDoc = baseDoc;
          //I don't want to talk about it.
          baseDoc = newDoc;
          this.fDocList = [newDoc];
          initialDiv = curDiv;
        }

        this.CopyFieldValues(newDoc, refreshInfo);

        //    newDoc.SetDeferLoadPics(true);
        w.FinishDocSetup(newDoc);
        //    newDoc.SetDeferLoadPics(false);
      }

      if (priorDocMgr) {
        var curDivName = null;
        var curDoc = priorDocMgr.GetCurrentDoc();
        if (curDoc) curDivName = curDoc.GetDivisionName();
        if (curDivName && curDivName.length > 0) {
          var doc = this.GetDocByDivName(curDivName);
          if (doc != null) initialDiv = curDivName;
        }
      } else {
        initialDiv = this.fRootDiv;
        this.fRootDiv = null;
      }

      //  var startDoc = this.GetCurrentDoc();
      //  startDoc.SetDeferLoadPics(true);
      w.SwitchToDivision(initialDiv, true);
      //  startDoc.SetDeferLoadPics(false);
    }

    this.ResetRefMgr();
    if (priorDocMgr) {
      this.ClearIWizDataDocMgrReference();
    }
    //if (!Globals._wsMgr.GetDoc().IsServerChart())
    //    this.StartAutoSave();
  }

  //=================================================================================================
  // Master Page Templates
  SetSaveAsMPTemplate(saveAsMPTemplate) {
    this.fOutputMPTemplate = saveAsMPTemplate;
  }

  GetSaveAsMPTemplate() {
    return this.fOutputMPTemplate;
  }

  GetPageMgr() {
    return this.fPageMgr;
  }

  GetBackgroundColor() {
    return this.fPageMgr.GetBackgroundColor();
  }

  SetBackgroundColor(bkGroundColor) {
    return this.fPageMgr.SetBackgroundColor(bkGroundColor);
  }

  GetBackgroundColor2() {
    return this.fPageMgr.GetBackgroundColor2();
  }

  SetBackgroundColor2(bkGroundColor) {
    return this.fPageMgr.SetBackgroundColor2(bkGroundColor);
  }

  GetBackgroundFillMode() {
    return this.fPageMgr.GetBackgroundFillMode();
  }

  SetBackgroundFillMode(fillMode) {
    return this.fPageMgr.SetBackgroundFillMode(fillMode);
  }

  //PINKFLAG
  InitMPTemplate() {
    var sc = this.GetSubChartMgr();

    var d = this.GetCurrentDoc();
    d.LoadSubCharts(null);
    sc.SetCurrentSubChart(kMasterSCID);
  }

  LoadMP(stream) {
    var useJSONLoad = this.LoadDocAsJSON(this.GetFileName(true));
    if (useJSONLoad) {
      var theData = JSON.parse(stream.fStreamBuffer[0]);
      this.LoadFromJSON(theData);
    } else {
      var loader = new QXMLLoader();
      var objTree = loader.LoadXML(stream);

      var rootElementName = objTree.GetElementName();
      var docTree = objTree;
      if (rootElementName == "qxdoc") {
        this.LoadDocument(objTree, false);
      } else this.LoadFromXML(objTree);
    }
  }

  // References
  ResolveReference(reference, dbObj) {
    return this.fRefMgr.ResolveReference(reference, dbObj);
  }

  ResetRefMgr() {
    var isActive = false;
    var r = this.fRefMgr;
    var iWiz = this.GetIWizDataRefresh();
    if (iWiz != null && iWiz.GetUseRefTable() == true) {
      isActive = true;
      var refFile = iWiz.GetRefTableFile();
      if (refFile != null && refFile.length > 4) {
        r.SetFileName(refFile);
        r.LoadRefMgr();
      }
    }

    r.SetActive(isActive);
    //  r.SetLoaded(true);
  }

  SaveLastSearch(lastSearch) {
    this.fLastSearch = lastSearch;
  }

  GetLastSearch() {
    return this.fLastSearch;
  }

  FinishAutoSave() {
    this.fSaveQueued = false;
    this.fSaveInProgress = false;
    this.fWSMgr?.UpdateDocContext();
  }

  CloseDocMgr() {
    this.FinishAutoSave();
    if (this.fWSMgr.fAutoSaveId) clearTimeout(this.fWSMgr.fAutoSaveId);
    if (this.fSaveQueueId != null) {
      clearTimeout(this.fSaveQueueId);
    }
  }

  FileResult(response, mode, entityType, directory, fileName) {
    //console.log('hello')
  }
}
